import React, { useState, useEffect } from 'react'
import { useApi } from '../../contexts/ApiContext'
import { useNavigate } from 'react-router-dom'
import { useUserId } from '../../hooks/useUserId'
import { 
  Users, 
  FileText, 
  CreditCard, 
  Building2,
  Database,
  Settings,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Activity,
  DollarSign,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  BarChart3,
  PieChart,
  Target,
  Shield,
  Heart,
  Car,
  Plane,
  Filter,
  RefreshCw,
  Bell,
  List
} from 'lucide-react'
import toast from 'react-hot-toast'

const AdminDashboard = () => {
  const { getDashboardStats, getFilteredData } = useApi()
  const navigate = useNavigate()
  const { userId, hasUserId, isFromUrl } = useUserId()
  const [stats, setStats] = useState({})
  const [loading, setLoading] = useState(true)
  const [monthlyData, setMonthlyData] = useState({
    claims: [],
    payments: [],
    claimsBreakdown: []
  })
  const [filters, setFilters] = useState({
    timeRange: 'ytd', // ytd, mtd, today, last_30_days, last_90_days, last_6_months, last_12_months, custom
    startDate: '',
    endDate: '',
    claimStatus: 'all', // all, DRAFT, SUBMITTED, UNDER_REVIEW, APPROVED, REJECTED, PAID, CLOSED
    paymentStatus: 'all', // all, PENDING, PROCESSING, PAID, FAILED
    showLegend: true
  })
  const [showFilterPanel, setShowFilterPanel] = useState(false)

  // Define all the table cards with their details
  const tableCards = [
    { name: 'Customers', href: '/admin/customers', icon: Users, color: 'blue', key: 'totalCustomers' },
    { name: 'Claims', href: '/admin/claims', icon: FileText, color: 'green', key: 'totalClaims' },
    { name: 'Policies', href: '/admin/policies', icon: CreditCard, color: 'purple', key: 'totalPolicies' },
    { name: 'Hospitals', href: '/admin/hospitals', icon: Building2, color: 'orange', key: 'totalHospitals' },
    { name: 'Diseases', href: '/admin/diseases', icon: Database, color: 'red', key: 'totalDiseases' },
    { name: 'Documents', href: '/admin/documents', icon: FileText, color: 'indigo', key: 'totalDocuments' },
    { name: 'Payments', href: '/admin/payments', icon: CreditCard, color: 'emerald', key: 'totalPayments' },
    { name: 'Policy Types', href: '/admin/policy-types', icon: Database, color: 'cyan', key: 'totalPolicyTypes' },
    { name: 'Coverage Rules', href: '/admin/coverage-rules', icon: Settings, color: 'amber', key: 'totalCoverageRules' },
    { name: 'Claim Decisions', href: '/admin/claim-decisions', icon: FileText, color: 'pink', key: 'totalClaimDecisions' },
    { name: 'Notifications', href: '/admin/notifications', icon: Bell, color: 'rose', key: 'totalNotifications' },
    { name: 'API Logs', href: '/admin/api-logs', icon: List, color: 'teal', key: 'totalApiLogs' },
    { name: 'Sessions', href: '/admin/sessions', icon: Activity, color: 'slate', key: 'totalSessions' },
    { name: 'Users', href: '/admin/users', icon: Users, color: 'violet', key: 'totalUsers' },
  ]

  useEffect(() => {
    console.log('Admin Dashboard - User ID:', userId)
    console.log('Admin Dashboard - Has User ID:', hasUserId)
    console.log('Admin Dashboard - Is from URL:', isFromUrl)
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUserId, userId])

  const loadData = async () => {
    try {
      setLoading(true)
      const statsData = await getDashboardStats()
      setStats(statsData)
      
      // Get real filtered data from database
      const filteredData = await getFilteredData(
        filters.timeRange,
        filters.startDate,
        filters.endDate,
        filters.claimStatus,
        filters.paymentStatus
      )
      
      // Use real data instead of generating fake data
      const generateMonthlyDataFromRealData = (realData, timeRange) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        const currentDate = new Date()
        const currentMonth = currentDate.getMonth()
        const data = []
        
        // Group real data by month
        const dataByMonth = {}
        realData.forEach(item => {
          const date = new Date(item.created_at)
          const month = date.getMonth()
          const monthName = months[month]
          
          if (!dataByMonth[monthName]) dataByMonth[monthName] = 0
          dataByMonth[monthName]++
        })
        
        // Determine which months to show based on time range
        let startMonth = 0
        let endMonth = currentMonth
        
        switch (timeRange) {
          case 'today':
          case 'mtd':
            startMonth = currentMonth
            endMonth = currentMonth
            break
          case 'ytd':
            startMonth = 0
            endMonth = currentMonth
            break
          case 'last_30_days':
            startMonth = Math.max(0, currentMonth - 1)
            endMonth = currentMonth
            break
          case 'last_90_days':
            startMonth = Math.max(0, currentMonth - 2)
            endMonth = currentMonth
            break
          case 'last_6_months':
            startMonth = Math.max(0, currentMonth - 5)
            endMonth = currentMonth
            break
          case 'last_12_months':
            startMonth = Math.max(0, currentMonth - 11)
            endMonth = currentMonth
            break
          default:
            break
        }
        
        // Generate data for the determined range
        for (let i = startMonth; i <= endMonth; i++) {
          const month = months[i]
          const value = dataByMonth[month] || 0
          data.push({ month, value })
        }
        
        return data
      }
      
      // Generate claims breakdown data from real data
      const generateClaimsBreakdown = (realClaimsData, timeRange, startDate, endDate) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        const currentDate = new Date()
        const currentMonth = currentDate.getMonth()
        const data = []
        
        let startMonth = 0
        let endMonth = currentMonth
        
        // Use same logic as generateMonthlyData
        switch (timeRange) {
          case 'today':
          case 'mtd':
            startMonth = currentMonth
            endMonth = currentMonth
            break
          case 'ytd':
            startMonth = 0
            endMonth = currentMonth
            break
          case 'last_30_days':
            startMonth = Math.max(0, currentMonth - 1)
            endMonth = currentMonth
            break
          case 'last_90_days':
            startMonth = Math.max(0, currentMonth - 2)
            endMonth = currentMonth
            break
          case 'last_6_months':
            startMonth = Math.max(0, currentMonth - 5)
            endMonth = currentMonth
            break
          case 'last_12_months':
            startMonth = Math.max(0, currentMonth - 11)
            endMonth = currentMonth
            break
          case 'custom':
            if (startDate && endDate) {
              const start = new Date(startDate)
              const end = new Date(endDate)
              startMonth = start.getMonth()
              endMonth = end.getMonth()
            }
            break
          default:
            break
        }
        
        // Group real claims data by month and status
        const claimsByMonth = {}
        realClaimsData.forEach(item => {
          const date = new Date(item.created_at)
          const month = date.getMonth()
          const monthName = months[month]
          
          if (!claimsByMonth[monthName]) {
            claimsByMonth[monthName] = { approved: 0, pending: 0, rejected: 0, total: 0 }
          }
          
          // Map claim status to our breakdown categories
          if (item.claim_status === 'APPROVED' || item.claim_status === 'PAID') {
            claimsByMonth[monthName].approved++
          } else if (
            item.claim_status === 'DRAFT' ||
            item.claim_status === 'SUBMITTED' ||
            item.claim_status === 'UNDER_REVIEW'
          ) {
            claimsByMonth[monthName].pending++
          } else if (item.claim_status === 'REJECTED') {
            claimsByMonth[monthName].rejected++
          }
          claimsByMonth[monthName].total++
        })
        
        for (let i = startMonth; i <= endMonth; i++) {
          const month = months[i]
          const monthData = claimsByMonth[month] || { approved: 0, pending: 0, rejected: 0, total: 0 }
          data.push({
            month,
            approved: monthData.approved,
            pending: monthData.pending,
            rejected: monthData.rejected,
            total: monthData.total
          })
        }
        return data
      }
      
      setMonthlyData({
        claims: generateMonthlyDataFromRealData(filteredData.claimsData, filters.timeRange),
        payments: generateMonthlyDataFromRealData(filteredData.paymentsData, filters.timeRange),
        claimsBreakdown: generateClaimsBreakdown(
          filteredData.claimsData,
          filters.timeRange,
          filters.startDate,
          filters.endDate
        )
      })
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load data')
      // Set fallback data to show something
      setStats({
        totalCustomers: 0,
        totalClaims: 0,
        totalPolicies: 0,
        totalHospitals: 0,
        totalDiseases: 0,
        totalDocuments: 0,
        totalPayments: 0,
        totalPolicyTypes: 0,
        totalCoverageRules: 0,
        totalClaimDecisions: 0,
        totalSessions: 0,
        totalUsers: 0
      })
      setMonthlyData({
        claims: [],
        payments: [],
        claimsBreakdown: []
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCardClick = (href) => {
    // Get user_id from current URL if not available in context
    const urlParams = new URLSearchParams(window.location.search)
    const currentUserId = urlParams.get('user_id') || userId
    
    const finalHref = currentUserId ? `${href}?user_id=${currentUserId}` : href
    console.log('Admin Navigation - Original href:', href, 'Final href:', finalHref, 'User ID:', currentUserId)
    navigate(finalHref)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600">Comprehensive overview of your insurance system</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Calendar className="h-4 w-4" />
            <span>{new Date().toLocaleDateString()}</span>
          </div>
          
          {/* Filter Toggle Button */}
          <div className="relative">
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
            >
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">Filters</span>
            </button>
            
            {/* Filter Panel */}
            {showFilterPanel && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 p-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Data Filters</h3>
                    <button
                      onClick={() => setShowFilterPanel(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </div>
                  
                  {/* Time Range Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Time Range</label>
                    <select
                      value={filters.timeRange}
                      onChange={(e) => setFilters({...filters, timeRange: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="ytd">Year to Date (YTD)</option>
                      <option value="mtd">Month to Date (MTD)</option>
                      <option value="today">Today</option>
                      <option value="last_30_days">Last 30 Days</option>
                      <option value="last_90_days">Last 90 Days</option>
                      <option value="last_6_months">Last 6 Months</option>
                      <option value="last_12_months">Last 12 Months</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>
                  
                  {/* Custom Date Range */}
                  {filters.timeRange === 'custom' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={filters.startDate}
                          onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={filters.endDate}
                          onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Claim Status Filter - Using Real Database Enum Values */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Claim Status</label>
                    <select
                      value={filters.claimStatus}
                      onChange={(e) => setFilters({...filters, claimStatus: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Claims</option>
                      <option value="DRAFT">Draft</option>
                      <option value="SUBMITTED">Submitted</option>
                      <option value="UNDER_REVIEW">Under Review</option>
                      <option value="APPROVED">Approved</option>
                      <option value="REJECTED">Rejected</option>
                      <option value="PAID">Paid</option>
                      <option value="CLOSED">Closed</option>
                    </select>
                  </div>
                  
                  {/* Payment Status Filter - Using Real Database Enum Values */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
                    <select
                      value={filters.paymentStatus}
                      onChange={(e) => setFilters({...filters, paymentStatus: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Payments</option>
                      <option value="PENDING">Pending</option>
                      <option value="PROCESSING">Processing</option>
                      <option value="PAID">Paid</option>
                      <option value="FAILED">Failed</option>
                    </select>
                  </div>
                  
                  {/* Chart Options */}
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={filters.showLegend}
                        onChange={(e) => setFilters({...filters, showLegend: e.target.checked})}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Show Legend</span>
                    </label>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex space-x-3 pt-2">
                    <button
                      onClick={() => {
                        setFilters({
                          timeRange: 'ytd',
                          startDate: '',
                          endDate: '',
                          claimStatus: 'all',
                          paymentStatus: 'all',
                          showLegend: true
                        })
                        loadData()
                      }}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                    >
                      <RefreshCw className="h-4 w-4" />
                      <span>Reset</span>
                    </button>
                    <button
                      onClick={() => {
                        loadData()
                        setShowFilterPanel(false)
                      }}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                    >
                      <Filter className="h-4 w-4" />
                      <span>Apply</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Business Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Claims Analysis - Dynamic Title */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {`Claims Analysis (${
                filters.timeRange === 'ytd' ? 'Year to Date' : 
                filters.timeRange === 'mtd' ? 'Month to Date' :
                filters.timeRange === 'today' ? 'Today' :
                filters.timeRange === 'last_30_days' ? 'Last 30 Days' :
                filters.timeRange === 'last_90_days' ? 'Last 90 Days' :
                filters.timeRange === 'last_6_months' ? 'Last 6 Months' :
                filters.timeRange === 'last_12_months' ? 'Last 12 Months' :
                filters.timeRange === 'custom' ? 'Custom Range' : 'Year to Date'
              })`}
            </h3>
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          
          {/* Monthly Claims Trend Chart */}
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-medium text-gray-600">
                {filters.claimStatus === 'all' ? 'Total Claims' : 
                 filters.claimStatus === 'DRAFT' ? 'Draft Claims' :
                 filters.claimStatus === 'SUBMITTED' ? 'Submitted Claims' :
                 filters.claimStatus === 'UNDER_REVIEW' ? 'Under Review Claims' :
                 filters.claimStatus === 'APPROVED' ? 'Approved Claims' :
                 filters.claimStatus === 'REJECTED' ? 'Rejected Claims' :
                 filters.claimStatus === 'PAID' ? 'Paid Claims' :
                 filters.claimStatus === 'CLOSED' ? 'Closed Claims' : 'Filtered Claims'}: {monthlyData.claims.reduce((sum, item) => sum + item.value, 0)}
              </span>
              <span className="text-sm text-gray-500">
                {filters.timeRange === 'ytd' ? 'Year to Date' : 
                 filters.timeRange === 'mtd' ? 'Month to Date' :
                 filters.timeRange === 'today' ? 'Today' :
                 filters.timeRange === 'last_30_days' ? 'Last 30 Days' :
                 filters.timeRange === 'last_90_days' ? 'Last 90 Days' :
                 filters.timeRange === 'last_6_months' ? 'Last 6 Months' :
                 filters.timeRange === 'last_12_months' ? 'Last 12 Months' :
                 filters.timeRange === 'custom' ? 'Custom Range' : 'Year to Date'}
              </span>
            </div>
            
            {/* Real Data Summary */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Records Found</span>
                <div className="flex items-center space-x-2">
                  <div className="w-20 bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                  <span className="text-sm font-medium text-blue-600">
                    {monthlyData.claims.reduce((sum, item) => sum + item.value, 0)} records
                  </span>
                </div>
              </div>
            </div>
            
            {/* Monthly Claims Breakdown - Professional Bar Chart with Axes */}
            <div className="mt-6">
              {/* Y-Axis Labels */}
              <div className="flex">
                <div className="w-8 flex flex-col justify-between h-32 text-xs text-gray-500">
                  {(() => {
                    const maxValue = Math.max(...monthlyData.claimsBreakdown.map(d => d.total), 1)
                    const steps = [maxValue, Math.floor(maxValue * 0.75), Math.floor(maxValue * 0.5), Math.floor(maxValue * 0.25), 0]
                    return steps.map((value, index) => (
                      <div key={index} className="text-right pr-1">{value}</div>
                    ))
                  })()}
                </div>
                
                {/* Chart Area with Border */}
                <div className="flex-1 relative">
                  {/* Y-Axis Line */}
                  <div className="absolute left-0 top-0 w-px h-32 bg-gray-300"></div>
                  
                  {/* X-Axis Line */}
                  <div className="absolute left-0 bottom-0 w-full h-px bg-gray-300"></div>
                  
                  {/* Grid Lines */}
                  {(() => {
                    const maxValue = Math.max(...monthlyData.claimsBreakdown.map(d => d.total), 1)
                    const steps = [0.25, 0.5, 0.75, 1]
                    return steps.map((ratio, index) => (
                      <div 
                        key={index}
                        className="absolute left-0 w-full h-px bg-gray-100"
                        style={{ bottom: `${ratio * 100}%` }}
                      ></div>
                    ))
                  })()}
                  
                  {/* Bars */}
                  <div className="flex items-end space-x-1 h-32 p-1">
                    {monthlyData.claimsBreakdown.map((item, index) => {
                      const maxValue = Math.max(...monthlyData.claimsBreakdown.map(d => d.total), 1)
                      const width = `${100 / monthlyData.claimsBreakdown.length}%`
                      
                      const approvedHeight = (item.approved / maxValue) * 100
                      const pendingHeight = (item.pending / maxValue) * 100
                      const rejectedHeight = (item.rejected / maxValue) * 100
                      
                      return (
                        <div key={index} className="flex flex-col justify-end" style={{ width }}>
                          {filters.claimStatus === 'all' ? (
                            <>
                              {/* Approved Claims - Green */}
                              {item.approved > 0 && (
                                <div 
                                  className="bg-green-500 rounded-t flex items-center justify-center text-xs text-white font-medium"
                                  style={{ 
                                    height: `${approvedHeight}%`,
                                    minHeight: approvedHeight > 15 ? '20px' : '0px'
                                  }}
                                  title={`${item.month} - Approved: ${item.approved}`}
                                >
                                  {approvedHeight > 15 && item.approved}
                                </div>
                              )}
                              
                              {/* Pending Claims - Yellow */}
                              {item.pending > 0 && (
                                <div 
                                  className="bg-yellow-500 flex items-center justify-center text-xs text-white font-medium"
                                  style={{ 
                                    height: `${pendingHeight}%`,
                                    minHeight: pendingHeight > 15 ? '20px' : '0px'
                                  }}
                                  title={`${item.month} - Pending: ${item.pending}`}
                                >
                                  {pendingHeight > 15 && item.pending}
                                </div>
                              )}
                              
                              {/* Rejected Claims - Red */}
                              {item.rejected > 0 && (
                                <div 
                                  className="bg-red-500 rounded-b flex items-center justify-center text-xs text-white font-medium"
                                  style={{ 
                                    height: `${rejectedHeight}%`,
                                    minHeight: rejectedHeight > 15 ? '20px' : '0px'
                                  }}
                                  title={`${item.month} - Rejected: ${item.rejected}`}
                                >
                                  {rejectedHeight > 15 && item.rejected}
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              {/* Single Status Bar */}
                              {(() => {
                                let value = 0
                                let color = 'bg-blue-500'
                                let title = ''
                                
                                if (filters.claimStatus === 'APPROVED') {
                                  value = item.approved
                                  color = 'bg-green-500'
                                  title = `${item.month} - Approved: ${item.approved}`
                                } else if (
                                  filters.claimStatus === 'DRAFT' ||
                                  filters.claimStatus === 'SUBMITTED' ||
                                  filters.claimStatus === 'UNDER_REVIEW'
                                ) {
                                  value = item.pending
                                  color = 'bg-yellow-500'
                                  title = `${item.month} - Pending: ${item.pending}`
                                } else if (filters.claimStatus === 'REJECTED') {
                                  value = item.rejected
                                  color = 'bg-red-500'
                                  title = `${item.month} - Rejected: ${item.rejected}`
                                } else {
                                  // For PAID, CLOSED, etc., show total
                                  value = item.total
                                  color = 'bg-blue-500'
                                  title = `${item.month} - Total: ${item.total}`
                                }
                                
                                const height = (value / maxValue) * 100
                                
                                return value > 0 ? (
                                  <div 
                                    className={`${color} rounded flex items-center justify-center text-xs text-white font-medium`}
                                    style={{ 
                                      height: `${height}%`,
                                      minHeight: height > 15 ? '20px' : '0px'
                                    }}
                                    title={title}
                                  >
                                    {height > 15 && value}
                                  </div>
                                ) : null
                              })()}
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
              
              {/* X-Axis Labels */}
              <div className="flex ml-8 mt-2">
                {monthlyData.claimsBreakdown.map((item, index) => (
                  <div key={index} className="flex-1 text-center text-xs text-gray-500">
                    {item.month}
                  </div>
                ))}
              </div>
            </div>
              
            {/* Real Data Legend */}
            {filters.showLegend && (
              <div className="flex justify-center space-x-6 mt-4 text-xs">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
                  <span>Real Database Records</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gray-400 rounded mr-2"></div>
                  <span>Based on Created Date</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment Trends - Dynamic Title */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {`Payment Trends (${
                filters.timeRange === 'ytd' ? 'Year to Date' : 
                filters.timeRange === 'mtd' ? 'Month to Date' :
                filters.timeRange === 'today' ? 'Today' :
                filters.timeRange === 'last_30_days' ? 'Last 30 Days' :
                filters.timeRange === 'last_90_days' ? 'Last 90 Days' :
                filters.timeRange === 'last_6_months' ? 'Last 6 Months' :
                filters.timeRange === 'last_12_months' ? 'Last 12 Months' :
                filters.timeRange === 'custom' ? 'Custom Range' : 'Year to Date'
              })`}
            </h3>
            <PieChart className="h-5 w-5 text-gray-400" />
          </div>
          
          {/* Payment Status Breakdown */}
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-medium text-gray-600">
                {filters.paymentStatus === 'all' ? 'Total Payments' : 
                 filters.paymentStatus === 'PENDING' ? 'Pending Payments' :
                 filters.paymentStatus === 'PROCESSING' ? 'Processing Payments' :
                 filters.paymentStatus === 'PAID' ? 'Paid Payments' :
                 filters.paymentStatus === 'FAILED' ? 'Failed Payments' : 'Filtered Payments'}: {monthlyData.payments.reduce((sum, item) => sum + item.value, 0)}
              </span>
              <span className="text-sm text-gray-500">
                {filters.timeRange === 'ytd' ? 'Year to Date' : 
                 filters.timeRange === 'mtd' ? 'Month to Date' :
                 filters.timeRange === 'today' ? 'Today' :
                 filters.timeRange === 'last_30_days' ? 'Last 30 Days' :
                 filters.timeRange === 'last_90_days' ? 'Last 90 Days' :
                 filters.timeRange === 'last_6_months' ? 'Last 6 Months' :
                 filters.timeRange === 'last_12_months' ? 'Last 12 Months' :
                 filters.timeRange === 'custom' ? 'Custom Range' : 'Year to Date'}
              </span>
            </div>
            
            {/* Real Payment Data Summary */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Payment Records Found</span>
                <div className="flex items-center space-x-2">
                  <div className="w-20 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                  <span className="text-sm font-medium text-green-600">
                    {monthlyData.payments.reduce((sum, item) => sum + item.value, 0)} records
                  </span>
                </div>
              </div>
            </div>
            
            {/* Revenue Trend Line - Professional Chart with Axes */}
            <div className="mt-6">
              {/* Y-Axis Labels */}
              <div className="flex">
                <div className="w-8 flex flex-col justify-between h-32 text-xs text-gray-500">
                  {(() => {
                    const maxValue = Math.max(...monthlyData.payments.map(d => d.value), 1)
                    const steps = [maxValue, Math.floor(maxValue * 0.75), Math.floor(maxValue * 0.5), Math.floor(maxValue * 0.25), 0]
                    return steps.map((value, index) => (
                      <div key={index} className="text-right pr-1">{value}</div>
                    ))
                  })()}
                </div>
                
                {/* Chart Area with Border */}
                <div className="flex-1 relative">
                  {/* Y-Axis Line */}
                  <div className="absolute left-0 top-0 w-px h-32 bg-gray-300"></div>
                  
                  {/* X-Axis Line */}
                  <div className="absolute left-0 bottom-0 w-full h-px bg-gray-300"></div>
                  
                  {/* Grid Lines */}
                  {(() => {
                    const maxValue = Math.max(...monthlyData.payments.map(d => d.value), 1)
                    const steps = [0.25, 0.5, 0.75, 1]
                    return steps.map((ratio, index) => (
                      <div 
                        key={index}
                        className="absolute left-0 w-full h-px bg-gray-100"
                        style={{ bottom: `${ratio * 100}%` }}
                      ></div>
                    ))
                  })()}
                  
                  {/* Line Chart */}
                  <div className="relative h-32 p-1">
                    <svg className="w-full h-full" viewBox="0 0 200 60">
                      {monthlyData.payments.length > 1 && (
                        <polyline
                          fill="none"
                          stroke="#10b981"
                          strokeWidth="2"
                          points={monthlyData.payments.map((item, index) => {
                            const maxValue = Math.max(...monthlyData.payments.map(d => d.value), 1)
                            const y = 50 - (item.value / maxValue) * 40
                            const x = index * 20 + 10
                            return `${x},${y}`
                          }).join(' ')}
                        />
                      )}
                      {monthlyData.payments.map((item, index) => {
                        const maxValue = Math.max(...monthlyData.payments.map(d => d.value), 1)
                        const y = 50 - (item.value / maxValue) * 40
                        const x = index * 20 + 10
                        return (
                          <circle key={index} cx={x} cy={y} r="2" fill="#10b981" />
                        )
                      })}
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* X-Axis Labels */}
              <div className="flex ml-8 mt-2">
                {monthlyData.payments.map((item, index) => (
                  <div key={index} className="flex-1 text-center text-xs text-gray-500">
                    {item.month}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Real Data Legend */}
            {filters.showLegend && (
              <div className="flex justify-center space-x-6 mt-4 text-xs">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
                  <span>Real Database Records</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gray-400 rounded mr-2"></div>
                  <span>Based on Created Date</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Database Management Section */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Database Management</h2>
        <p className="text-gray-600">Manage and monitor all system data</p>
      </div>

      {/* Table Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {tableCards.map((card) => {
          const Icon = card.icon
          const count = stats[card.key] || 0
          
          return (
            <div
              key={card.name}
              onClick={() => handleCardClick(card.href)}
              className="card cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`p-3 rounded-lg bg-${card.color}-100`}>
                    <Icon className={`h-6 w-6 text-${card.color}-600`} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{card.name}</p>
                    <p className="text-2xl font-bold text-gray-900">{count}</p>
                  </div>
                </div>
                <div className="text-gray-400">
                  <Eye className="h-5 w-5" />
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default AdminDashboard
